import { useMemo, useState } from 'react';
import { CheckCircle2, ClipboardCopy } from 'lucide-react';
import clsx from 'clsx';
import { ToolHeader } from '../components/ToolHeader';
import {
  CASE_KINDS,
  CASE_LABEL,
  toAllCases,
  type CaseKind,
} from '../lib/textCase';

const SAMPLE_INPUT = 'helloWorldFoo BarHTTPRequest v1Beta_test 中文 Mix';

/**
 * `/tools/convert/text-case` — text case converter.
 *
 * One textarea on top, 9 result rows below. Each row displays both the
 * label (`camelCase`) and the rendered output, with a per-row copy
 * button. Live computation — pure functions, no debounce needed.
 */
export default function TextCaseTool(): JSX.Element {
  const [input, setInput] = useState<string>(SAMPLE_INPUT);
  const cases = useMemo(() => toAllCases(input), [input]);

  return (
    <div className="flex h-full min-h-0 flex-col gap-3 p-4">
      <ToolHeader
        toolId="text-case"
        category="convert"
        description="9 种命名风格一次性转换：camel / pascal / kebab / snake / SCREAMING_SNAKE / dot / path / Sentence / Title。Token 化后再 join，对 v1Beta、HTTPRequest 等数字与缩写边界有正确切分。"
      />

      <section className="card-pad flex flex-col gap-2">
        <header className="flex items-center justify-between">
          <span className="text-[11px] uppercase tracking-wide text-ink-dim">
            输入
          </span>
          <span className="text-[11px] text-ink-dim">{input.length} 字符</span>
        </header>
        <textarea
          className="input min-h-[80px] resize-none font-mono text-[13px]"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          spellCheck={false}
          placeholder="输入任意文本，下方实时显示 9 种风格"
        />
      </section>

      <section className="card-pad flex min-h-0 flex-1 flex-col gap-1 overflow-auto">
        <header className="text-[11px] uppercase tracking-wide text-ink-dim">
          输出（9 种）
        </header>
        <ul className="divide-y divide-line/40">
          {CASE_KINDS.map((k) => (
            <CaseRow key={k} kind={k} value={cases[k]} />
          ))}
        </ul>
      </section>
    </div>
  );
}

function CaseRow({
  kind,
  value,
}: {
  kind: CaseKind;
  value: string;
}): JSX.Element {
  const [copied, setCopied] = useState(false);
  const onCopy = async (): Promise<void> => {
    if (!value) return;
    try {
      await navigator.clipboard.writeText(value);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 900);
    } catch {
      /* clipboard write may fail on insecure context — silent ok */
    }
  };
  return (
    <li className="group flex items-center gap-3 px-2 py-1.5 hover:bg-bg-soft">
      <span className="w-32 shrink-0 text-[11px] text-ink-dim">
        {CASE_LABEL[kind]}
      </span>
      <code className="min-w-0 flex-1 select-text break-all font-mono text-[12.5px] text-ink">
        {value || <em className="text-ink-dim">(empty)</em>}
      </code>
      <button
        type="button"
        onClick={onCopy}
        disabled={!value}
        className={clsx(
          'shrink-0 rounded p-0.5 text-ink-dim transition opacity-0 group-hover:opacity-100',
          'hover:bg-bg-card hover:text-accent disabled:cursor-not-allowed disabled:opacity-30',
        )}
        title="复制"
      >
        {copied ? (
          <CheckCircle2 className="h-3 w-3 text-emerald-500" />
        ) : (
          <ClipboardCopy className="h-3 w-3" />
        )}
      </button>
    </li>
  );
}
